
import { useCallback, useEffect, useMemo, useState } from 'react';

import { buildFeed, orphanRows } from './feed';
import type { PlacementItem, PlacementMedia, PlacementProposalView } from './types';

export interface ProposalView {
  feed: ReturnType<typeof buildFeed>;
  mediaOf: (order: number) => PlacementMedia | null;
  overriddenOrders: ReadonlySet<number>;
  selectedOrder: number | null;
  select: (order: number) => void;
  selectedItem: PlacementItem | null;
  choose: (media: PlacementMedia) => void;
  resetChoice: () => void;
  reset: () => void;
}

export function useProposalView(proposal: PlacementProposalView | null): ProposalView {
  const [selectedOrder, setSelectedOrder] = useState<number | null>(null);
  const [overrides, setOverrides] = useState<Record<number, string>>({});

  useEffect(() => {
    if (selectedOrder !== null || !proposal) return;
    const first = proposal.items[0];
    if (first) setSelectedOrder(first.order);
  }, [proposal, selectedOrder]);

  const feed = useMemo(() => (proposal ? buildFeed(proposal) : []), [proposal]);
  const orphans = useMemo(
    () => (proposal ? orphanRows(proposal, feed) : []),
    [proposal, feed],
  );

  const mediaOf = useCallback(
    (order: number): PlacementMedia | null => {
      const item = proposal?.items.find((candidate) => candidate.order === order);
      if (!item) return null;
      const overrideId = overrides[order];
      if (!overrideId) return item.media;
      return item.alternatives.find((media) => media.itemId === overrideId) ?? item.media;
    },
    [overrides, proposal],
  );

  const overriddenOrders = useMemo(
    () => new Set(Object.keys(overrides).map(Number)),
    [overrides],
  );

  const selectedItem = proposal?.items.find((item) => item.order === selectedOrder) ?? null;

  const choose = useCallback(
    (media: PlacementMedia) => {
      if (!selectedItem) return;
      setOverrides((prev) => ({ ...prev, [selectedItem.order]: media.itemId }));
    },
    [selectedItem],
  );

  const resetChoice = useCallback(() => {
    if (!selectedItem) return;
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[selectedItem.order];
      return next;
    });
  }, [selectedItem]);

  const reset = useCallback(() => {
    setOverrides({});
    setSelectedOrder(null);
  }, []);

  return {
    feed: [...feed, ...orphans],
    mediaOf,
    overriddenOrders,
    selectedOrder,
    select: setSelectedOrder,
    selectedItem,
    choose,
    resetChoice,
    reset,
  };
}
